/**
 * Ingestion: the keys other systems send with, and what has arrived.
 *
 * One screen decision worth defending. **The key is shown once, in a panel that will not come
 * back**, because only its SHA-256 is stored — no later API call and no database read can produce
 * it. That is deliberately inconvenient. The alternative, a key retrievable whenever somebody
 * asks, means a single compromised operator session hands over every integration's credential at
 * once.
 *
 * The events table shows deduplicated and correlated arrivals alongside created ones. Hiding them
 * would make a major incident look like a single alert, when the useful fact is "we received forty
 * of these and raised one ticket".
 */

import { useCallback, useState, type JSX, type SyntheticEvent } from 'react';

import { api } from '../shared/api/client';
import {
  Badge,
  Card,
  ErrorBox,
  Formula,
  PageHeader,
  Spinner,
  Stat,
  StatRow,
  Table,
  type Tone,
} from '../shared/ui/ui';
import { useAsync } from '../shared/ui/useAsync';

const DISPOSITION_TONE: Record<string, Tone> = {
  created: 'ok',
  deduplicated: 'muted',
  correlated: 'info',
};

export function Ingestion(): JSX.Element {
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => {
    setRevision((r) => r + 1);
  }, []);

  const sources = useAsync(() => api.ingestionSources(), [revision]);
  const events = useAsync(() => api.ingestionEvents(200), [revision]);

  const [newKey, setNewKey] = useState<string | null>(null);
  const [writeError, setWriteError] = useState<unknown>(null);

  const rows = sources.data ?? [];
  const live = rows.filter((s) => !s.revoked);
  const silent = live.filter((s) => s.last_used_at === null).length;
  const arrivals = events.data ?? [];
  const suppressed = arrivals.filter((e) => e.disposition !== 'created').length;

  const revoke = (id: string): void => {
    setWriteError(null);
    void api
      .revokeIngestionSource(id)
      .then(refresh)
      .catch((e: unknown) => {
        setWriteError(e);
      });
  };

  return (
    <>
      <PageHeader
        title="Ingestion"
        subtitle="What other systems are allowed to raise a ticket, and what they have sent. A key raises tickets in one tenant and can do nothing else."
      />

      <ErrorBox error={sources.error} />
      <ErrorBox error={events.error} />
      <ErrorBox error={writeError} />

      <Card>
        <StatRow>
          <Stat label="Live sources" value={live.length} />
          <Stat
            label="Never used"
            value={silent}
            tone={silent > 0 ? 'warn' : 'neutral'}
            hint="configured but nothing has arrived"
          />
          <Stat label="Events received" value={arrivals.length} />
          <Stat
            label="Suppressed"
            value={suppressed}
            hint="deduplicated or correlated rather than raised again"
          />
        </StatRow>
      </Card>

      {newKey !== null && (
        <Card title="The key">
          <div className="alert alert--danger">
            <strong>Copy this now. It is not shown again and cannot be recovered.</strong> Only its
            hash is stored, so neither a database read nor a later API call can produce it. If it is
            lost, revoke this source and create another.
          </div>
          <Formula>{newKey}</Formula>
          <div className="inline-form">
            <button
              type="button"
              className="button button--small"
              onClick={() => {
                setNewKey(null);
              }}
            >
              I have copied it
            </button>
          </div>
        </Card>
      )}

      <MintKey
        onMinted={(key) => {
          setNewKey(key);
          refresh();
        }}
      />

      <Card
        title="Sources"
        subtitle="A source that has never sent anything is worth chasing: alerts that stop arriving look exactly like an estate with nothing wrong."
      >
        {sources.loading ? (
          <Spinner />
        ) : (
          <Table
            columns={['Name', 'Sending', 'Key', 'Last used', 'Events', '']}
            empty="No sources yet. Mint a key above and configure it at the far end."
          >
            {rows.map((s) => (
              <tr key={s.id}>
                <td>
                  <div className="table__strong">{s.name}</div>
                  {s.revoked && <Badge tone="muted">revoked</Badge>}
                </td>
                <td>
                  <Formula>{s.kind}</Formula>
                </td>
                <td>
                  <Formula>{s.key_prefix}…</Formula>
                </td>
                <td>
                  {s.last_used_at === null ? (
                    <Badge tone="warn">never</Badge>
                  ) : (
                    <Formula>{s.last_used_at.replace('T', ' ').slice(0, 16)}</Formula>
                  )}
                </td>
                <td className="table__num">{s.event_count}</td>
                <td>
                  {!s.revoked && (
                    <button
                      type="button"
                      className="button button--small"
                      onClick={() => {
                        revoke(s.id);
                      }}
                    >
                      revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card
        title="What has arrived"
        subtitle="Including what was deduplicated or correlated away — that is the difference between forty alerts and forty tickets."
      >
        {events.loading ? (
          <Spinner />
        ) : (
          <Table
            columns={['Received', 'Source', 'Event', 'About', 'Outcome', 'Ticket']}
            empty="Nothing has arrived yet."
          >
            {arrivals.map((e) => (
              <tr key={`${e.external_id}-${e.received_at}`}>
                <td>
                  <Formula>{e.received_at.replace('T', ' ').slice(0, 16)}</Formula>
                </td>
                <td>{e.source}</td>
                <td>
                  <Formula>{e.external_id}</Formula>
                </td>
                <td>
                  {/* The normalized values — what actually decided correlation. Rendered as text;
                      every field here is written by a system Conduit does not control. */}
                  <div className="table__strong">{e.asset}</div>
                  <Formula>{e.signal}</Formula>
                </td>
                <td>
                  <Badge tone={DISPOSITION_TONE[e.disposition] ?? 'muted'}>{e.disposition}</Badge>
                </td>
                <td>
                  <Formula>{e.reference ?? '—'}</Formula>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </>
  );
}

/** Minting a key. A tenant-admin act, because it creates a credential. */
function MintKey({ onMinted }: { onMinted: (key: string) => void }): JSX.Element {
  const [name, setName] = useState('');
  const [kind, setKind] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    setError(null);
    setBusy(true);

    void api
      .createIngestionSource(name.trim(), kind.trim().toLowerCase())
      .then((created) => {
        setName('');
        setKind('');
        onMinted(created.key);
      })
      .catch((e: unknown) => {
        setError(e);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Card
      title="Mint a key"
      subtitle="The kind also namespaces deduplication, so two systems that both number their events from 1 do not collide."
    >
      <ErrorBox error={error} />
      <form className="inline-form" onSubmit={submit}>
        <input
          className="input"
          placeholder="Name, e.g. Northwind Zabbix"
          value={name}
          maxLength={128}
          required
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
        <input
          className="input"
          placeholder="Kind, e.g. zabbix"
          value={kind}
          maxLength={32}
          required
          onChange={(e) => {
            setKind(e.target.value);
          }}
        />
        <button
          type="submit"
          className="button"
          disabled={busy || name.trim() === '' || kind.trim() === ''}
        >
          {busy ? 'Minting…' : 'Mint'}
        </button>
      </form>
    </Card>
  );
}
